import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { UtilsService } from 'src/app/services/utils.service';
import { OkrManagementCompanyPeriod } from 'src/app/models/okr-management.model';

const MIN_PERIOD_DURATION_DAYS = 75;
const MAX_PERIOD_DURATION_DAYS = 365;

export interface CompanyObjectivePeriodDialogData {
  mode?: 'create' | 'edit';
  start_date?: string | null;
  end_date?: string | null;
  existing_periods?: OkrManagementCompanyPeriod[];
}

export interface CompanyObjectivePeriodDialogResult {
  start_date: string;
  end_date: string;
}

@Component({
  selector: 'app-company-objective-period-dialog',
  templateUrl: './company-objective-period-dialog.component.html',
  styleUrl: './company-objective-period-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    CdkScrollable,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatRadioModule,
  ],
})
export class CompanyObjectivePeriodDialogComponent {
  private readonly dialog_ref = inject(MatDialogRef<CompanyObjectivePeriodDialogComponent, CompanyObjectivePeriodDialogResult>);
  private readonly fb = inject(FormBuilder);
  private readonly utils = inject(UtilsService);
  readonly data = inject<CompanyObjectivePeriodDialogData>(MAT_DIALOG_DATA);

  readonly is_edit_mode = this.data.mode === 'edit';
  readonly preset_options = this.buildPresetOptions(this.data.existing_periods ?? []);

  readonly form = this.fb.group(
    {
      start_date: [this.parseDate(this.data.start_date), Validators.required],
      end_date: [this.parseDate(this.data.end_date), Validators.required],
    },
    { validators: [periodNotFullyEndedValidator, periodDurationValidator] }
  );

  readonly selected_preset_id = signal(this.resolveInitialPresetId());
  readonly show_custom_range = computed(() => this.selected_preset_id() === 'custom');
  readonly min_period_duration_days = MIN_PERIOD_DURATION_DAYS;
  readonly max_period_duration_days = MAX_PERIOD_DURATION_DAYS;

  selectPreset(option_id: string): void {
    const option = this.preset_options.find(preset => preset.id === option_id);
    if (option == null || option.disabled) {
      return;
    }

    this.selected_preset_id.set(option_id);

    if (option_id === 'custom') {
      return;
    }

    this.form.patchValue({
      start_date: option.start_date,
      end_date: option.end_date,
    });
    this.form.markAsPristine();
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const start_date = this.form.controls.start_date.value;
    const end_date = this.form.controls.end_date.value;
    if (!(start_date instanceof Date) || !(end_date instanceof Date)) {
      return;
    }

    this.dialog_ref.close({
      start_date: this.utils.dateToStringYYYYMMDD(start_date),
      end_date: this.utils.dateToStringYYYYMMDD(end_date),
    });
  }

  close(): void {
    this.dialog_ref.close();
  }

  private resolveInitialPresetId(): string {
    const start_date = this.parseDate(this.data.start_date);
    const end_date = this.parseDate(this.data.end_date);

    if (start_date != null && end_date != null) {
      const current_key = periodRangeKey(start_date, end_date);
      const matching_preset = this.preset_options.find(
        option =>
          option.id !== 'custom' &&
          option.start_date != null &&
          option.end_date != null &&
          periodRangeKey(option.start_date, option.end_date) === current_key
      );

      if (matching_preset != null) {
        return matching_preset.id;
      }

      if (this.is_edit_mode) {
        return 'custom';
      }
    }

    if (this.is_edit_mode) {
      return 'custom';
    }

    const first_enabled = this.preset_options.find(option => !option.disabled && option.id !== 'custom');
    if (first_enabled != null) {
      this.form.patchValue({
        start_date: first_enabled.start_date,
        end_date: first_enabled.end_date,
      });

      return first_enabled.id;
    }

    return 'custom';
  }

  private buildPresetOptions(existing_periods: OkrManagementCompanyPeriod[]): PeriodPresetOption[] {
    const existing_keys = new Set(
      existing_periods.map(period => periodRangeKey(toDateOnly(period.start_date), toDateOnly(period.end_date)))
    );
    const today = startOfLocalDay(new Date());
    const current_year = today.getFullYear();
    const current_quarter = Math.floor(today.getMonth() / 3) + 1;

    const options: PeriodPresetOption[] = [
      this.buildAnnualOption(current_year, existing_keys),
      this.buildAnnualOption(current_year + 1, existing_keys),
    ];

    for (let offset = 0; offset < 3; offset++) {
      const quarter_index = current_quarter - 1 + offset;
      const year = current_year + Math.floor(quarter_index / 4);
      const quarter = (quarter_index % 4) + 1;
      options.push(this.buildQuarterOption(year, quarter, existing_keys));
    }

    options.push({
      id: 'custom',
      label: 'Personalizado',
      start_date: null,
      end_date: null,
      disabled: false,
      is_active: false,
    });

    return options;
  }

  private buildAnnualOption(year: number, existing_keys: Set<string>): PeriodPresetOption {
    const start_date = new Date(year, 0, 1);
    const end_date = new Date(year, 11, 31);
    const today = startOfLocalDay(new Date());

    return {
      id: `annual-${year}`,
      label: `Annual ${year}`,
      start_date,
      end_date,
      disabled: existing_keys.has(periodRangeKey(start_date, end_date)),
      is_active: isDateWithinInclusiveRange(today, start_date, end_date),
    };
  }

  private buildQuarterOption(year: number, quarter: number, existing_keys: Set<string>): PeriodPresetOption {
    const start_month = (quarter - 1) * 3;
    const start_date = new Date(year, start_month, 1);
    const end_date = new Date(year, start_month + 3, 0);
    const today = startOfLocalDay(new Date());

    return {
      id: `quarter-${year}-${quarter}`,
      label: `Q${quarter} ${year}`,
      start_date,
      end_date,
      disabled: existing_keys.has(periodRangeKey(start_date, end_date)),
      is_active: isDateWithinInclusiveRange(today, start_date, end_date),
    };
  }

  private parseDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    return toDateOnly(value);
  }
}

function periodNotFullyEndedValidator(control: AbstractControl): ValidationErrors | null {
  const start_date = control.get('start_date')?.value;
  const end_date = control.get('end_date')?.value;
  if (!(start_date instanceof Date) || !(end_date instanceof Date)) {
    return null;
  }

  const today = startOfLocalDay(new Date());
  const end = startOfLocalDay(end_date);

  if (end < today) {
    return { period_fully_ended: true };
  }

  if (startOfLocalDay(start_date) > end) {
    return { period_inverted: true };
  }

  return null;
}

function periodDurationValidator(control: AbstractControl): ValidationErrors | null {
  const start_date = control.get('start_date')?.value;
  const end_date = control.get('end_date')?.value;
  if (!(start_date instanceof Date) || !(end_date instanceof Date)) {
    return null;
  }

  if (startOfLocalDay(start_date) > startOfLocalDay(end_date)) {
    return null;
  }

  const duration_days = inclusiveDayCount(start_date, end_date);
  if (duration_days < MIN_PERIOD_DURATION_DAYS) {
    return { period_too_short: true };
  }

  if (duration_days > MAX_PERIOD_DURATION_DAYS) {
    return { period_too_long: true };
  }

  return null;
}

function inclusiveDayCount(start_date: Date, end_date: Date): number {
  const start_utc = Date.UTC(start_date.getFullYear(), start_date.getMonth(), start_date.getDate());
  const end_utc = Date.UTC(end_date.getFullYear(), end_date.getMonth(), end_date.getDate());

  return Math.round((end_utc - start_utc) / 86_400_000) + 1;
}

function periodRangeKey(start_date: Date, end_date: Date): string {
  return `${formatDateKey(start_date)}|${formatDateKey(end_date)}`;
}

function formatDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function toDateOnly(value: string | Date): Date {
  if (value instanceof Date) {
    return startOfLocalDay(value);
  }

  const date_only = value.slice(0, 10);
  const [year, month, day] = date_only.split('-').map(part => Number(part));

  return new Date(year, month - 1, day);
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function isDateWithinInclusiveRange(date: Date, start_date: Date, end_date: Date): boolean {
  const day = startOfLocalDay(date).getTime();
  const start = startOfLocalDay(start_date).getTime();
  const end = startOfLocalDay(end_date).getTime();

  return day >= start && day <= end;
}

interface PeriodPresetOption {
  id: string;
  label: string;
  start_date: Date | null;
  end_date: Date | null;
  disabled: boolean;
  is_active: boolean;
}
